import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { doc, getDoc, updateDoc, collection, getDocs, deleteDoc, query, orderBy } from 'firebase/firestore';
import CardManager from './CardManager';
import IconRenderer from '../ui/IconRenderer';
import { ReactComponent as BookIcon } from '../../Icons/book.svg';
import { ReactComponent as DeckIcon } from '../../Icons/deck-icon.svg';
import { ReactComponent as AddIcon } from '../../Icons/add.svg';
import '../Achievements/UserAchievementStatsPanel.css';
import '../Auxiliary/UserCreation.css';
import './DeckEditor.css';

function deckEditorLanguageIconName(lang) {
  if (!lang || typeof lang !== 'string') return 'javascript';
  const t = lang.trim().toLowerCase();
  if (t === 'html/css') return 'html';
  if (t === 'c#') return 'csharp';
  if (t === 'c++') return 'cpp';
  if (t === 'sql') return 'sql';
  if (t === 'shell' || t === 'other') return 'javascript';
  return t.replace(/\s+/g, '');
}

const PROGRAMMING_LANGUAGES = [
  'JavaScript',
  'Python',
  'Java',
  'C++',
  'C#',
  'Ruby',
  'PHP',
  'Swift',
  'Go',
  'Rust',
  'TypeScript',
  'SQL',
  'HTML/CSS',
  'Shell',
  'Other'
];

const DeckEditor = ({ userId, deckId }) => {
  const [deck, setDeck] = useState(null);
  const [deckName, setDeckName] = useState('');
  const [language, setLanguage] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [error, setError] = useState('');
  const [cards, setCards] = useState([]);
  const [editingCard, setEditingCard] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const languageDropdownRef = useRef(null);

  useEffect(() => {
    loadDeck();
    loadCards();
  }, [userId, deckId]);

  useEffect(() => {
    const onDown = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const loadDeck = async () => {
    try {
      const deckRef = doc(db, `users/${userId}/decks/${deckId}`);
      const deckDoc = await getDoc(deckRef);

      if (!deckDoc.exists()) {
        setError('Deck not found');
        return;
      }

      const deckData = deckDoc.data();
      setDeck(deckData);
      setDeckName(deckData.name);
      setLanguage(deckData.language || '');
      setDescription(deckData.description || '');
      setTags(deckData.tags ? deckData.tags.join(', ') : '');
      setError('');
    } catch (error) {
      console.error('Error loading deck:', error);
      setError('Failed to load deck');
    }
  };

  const loadCards = async () => {
    try {
      const cardsRef = collection(db, `users/${userId}/decks/${deckId}/cards`);
      const q = query(cardsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const loadedCards = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCards(loadedCards);
    } catch (error) {
      console.error('Error loading cards:', error);
      setError('Failed to load cards');
    }
  };

  const handleSave = async () => {
    try {
      const deckRef = doc(db, `users/${userId}/decks/${deckId}`);
      await updateDoc(deckRef, {
        name: deckName.trim(),
        language: language,
        description: description.trim(),
        tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        updatedAt: new Date().toISOString()
      });
      loadDeck();
    } catch (error) {
      console.error('Error updating deck:', error);
      setError('Failed to update deck');
    }
  };

  const deleteCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this card?')) return;
    try {
      // First delete the card's progress data
      try {
        await deleteDoc(doc(db, `users/${userId}/progress/${deckId}_${cardId}`));
      } catch (error) {
        console.error('Error deleting card progress:', error);
        // Continue with deletion even if progress deletion fails
      }

      // Then delete the card itself
      await deleteDoc(doc(db, `users/${userId}/decks/${deckId}/cards/${cardId}`));
      loadCards();
      if (editingCard?.id === cardId) {
        setEditingCard(null);
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      setError('Failed to delete card');
    }
  };

  const handleCardAdded = () => {
    setEditingCard(null);
    loadCards();
  };

  if (error && !deck) {
    return (
      <div className="full-page-creator deck-editor">
        <div className="creator-content creator-content--flashcard-form">
          <div className="deck-editor-error-panel">
            <div className="error-message" role="alert">{error}</div>
            <p className="deck-editor-error-hint">
              Use the <strong>Library</strong> item in the sidebar to return to your cookbooks.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="full-page-creator deck-editor">
        <div className="creator-content creator-content--flashcard-form">
          <div className="deck-editor-loading">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="full-page-creator deck-editor">
      <div className="creator-content creator-content--flashcard-form">
        <div className="full-page-creator-shell">
          <div className="full-page-creator-editor-column">
            <div className="full-page-creator-flashcard-wrap">
              <div className="full-page-creator-flashcard-card full-page-creator-flashcard-card--form full-page-creator-flashcard-card--metadata-flow deck-editor-flashcard-card">
                <header className="full-page-creator-flashcard-header full-page-creator-flashcard-header--with-checklist deck-editor-flashcard-header">
                  <div className="full-page-creator-flashcard-header-top deck-editor-flashcard-header-top">
                    <div className="full-page-creator-flashcard-title-block">
                      <div className="full-page-creator-flashcard-title-row">
                        <BookIcon className="full-page-creator-flashcard-title-icon" aria-hidden />
                        <h2 className="full-page-creator-flashcard-title">
                          {deckName?.trim() ? deckName : 'Cookbook'}
                        </h2>
                      </div>
                      <p className="full-page-creator-flashcard-subtitle">
                        Edit cookbook settings, review snippets, or add a new snippet.
                      </p>
                    </div>
                  </div>
                  <nav className="full-page-creator-creation-checklist" aria-label="Cookbook sections">
                    <ol className="full-page-creator-creation-checklist-track">
                      <li className="full-page-creator-creation-checklist-item">
                        <button
                          type="button"
                          className={[
                            'full-page-creator-creation-checklist-pill',
                            activeTab === 'details' ? 'full-page-creator-creation-checklist-pill--current' : '',
                          ].filter(Boolean).join(' ')}
                          onClick={() => {
                            setActiveTab('details');
                          }}
                        >
                          <span className="full-page-creator-creation-checklist-badge" aria-hidden="true">1</span>
                          <BookIcon className="full-page-creator-creation-checklist-icon" aria-hidden />
                          <span className="full-page-creator-creation-checklist-label">Details</span>
                        </button>
                      </li>
                      <li className="full-page-creator-creation-checklist-connector" aria-hidden="true" />
                      <li className="full-page-creator-creation-checklist-item">
                        <button
                          type="button"
                          className={[
                            'full-page-creator-creation-checklist-pill',
                            activeTab === 'cards' ? 'full-page-creator-creation-checklist-pill--current' : '',
                          ].filter(Boolean).join(' ')}
                          onClick={() => {
                            setActiveTab('cards');
                          }}
                        >
                          <span className="full-page-creator-creation-checklist-badge" aria-hidden="true">2</span>
                          <DeckIcon className="full-page-creator-creation-checklist-icon" aria-hidden />
                          <span className="full-page-creator-creation-checklist-label">Snippets</span>
                        </button>
                      </li>
                      <li className="full-page-creator-creation-checklist-connector" aria-hidden="true" />
                      <li className="full-page-creator-creation-checklist-item">
                        <button
                          type="button"
                          className={[
                            'full-page-creator-creation-checklist-pill',
                            activeTab === 'add' ? 'full-page-creator-creation-checklist-pill--current' : '',
                          ].filter(Boolean).join(' ')}
                          onClick={() => {
                            setActiveTab('add');
                          }}
                        >
                          <span className="full-page-creator-creation-checklist-badge" aria-hidden="true">3</span>
                          <AddIcon className="full-page-creator-creation-checklist-icon" aria-hidden />
                          <span className="full-page-creator-creation-checklist-label">Add snippet</span>
                        </button>
                      </li>
                    </ol>
                  </nav>
                </header>

                <div className="full-page-creator-flashcard-body full-page-creator-flashcard-body--metadata deck-editor-flashcard-body">
                  {error && (
                    <div className="deck-editor-inline-error error-message" role="alert">
                      {error}
                    </div>
                  )}

                  {activeTab === 'details' && (
                    <div className="deck-settings full-page-form full-page-form--flashcard-embedded">
                      <div className="form-group">
                        <label htmlFor="deck-editor-cookbook-name">Cookbook name</label>
                        <input
                          id="deck-editor-cookbook-name"
                          type="text"
                          value={deckName}
                          onChange={(e) => setDeckName(e.target.value)}
                          className="divider-input"
                          placeholder="Enter cookbook name"
                          autoComplete="off"
                        />
                      </div>

                      <div className="language-selector">
                        <label htmlFor="deck-editor-language-trigger">Programming language</label>
                        <div className="cookbook-language-selector-container" ref={languageDropdownRef}>
                          <div
                            id="deck-editor-language-trigger"
                            className={`cookbook-selected-language ${showLanguageDropdown ? 'open' : ''}`}
                            onClick={() => setShowLanguageDropdown((o) => !o)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowLanguageDropdown((o) => !o);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-expanded={showLanguageDropdown}
                            aria-haspopup="listbox"
                            aria-label="Programming language"
                          >
                            <div className="full-page-creator-selected-language">
                              <IconRenderer
                                name={deckEditorLanguageIconName(language)}
                                type="language"
                                size="small"
                                className="language-icon"
                              />
                              <span>
                                {language || 'Select language'}
                              </span>
                            </div>
                            <span className="dropdown-arrow">▼</span>
                          </div>
                          {showLanguageDropdown && (
                            <div className="cookbook-language-dropdown" role="listbox" aria-label="Language options">
                              <div
                                role="option"
                                aria-selected={language === ''}
                                className={`cookbook-language-option ${language === '' ? 'selected' : ''}`}
                                onClick={() => {
                                  setLanguage('');
                                  setShowLanguageDropdown(false);
                                }}
                              >
                                <span>Select language</span>
                              </div>
                              {PROGRAMMING_LANGUAGES.map((lang) => (
                                <div
                                  key={lang}
                                  role="option"
                                  aria-selected={language === lang}
                                  className={`cookbook-language-option ${language === lang ? 'selected' : ''}`}
                                  onClick={() => {
                                    setLanguage(lang);
                                    setShowLanguageDropdown(false);
                                  }}
                                >
                                  <IconRenderer
                                    name={deckEditorLanguageIconName(lang)}
                                    type="language"
                                    size="small"
                                    className="language-icon"
                                  />
                                  <span>{lang}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="deck-editor-description">Description</label>
                        <textarea
                          id="deck-editor-description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className="divider-input"
                          placeholder="Describe what this cookbook covers…"
                          rows={4}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="deck-editor-tags">Tags (comma-separated)</label>
                        <input
                          id="deck-editor-tags"
                          type="text"
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                          className="divider-input"
                          placeholder="e.g., algorithms, data structures"
                          autoComplete="off"
                        />
                      </div>

                      <div className="form-action-center">
                        <button type="button" className="submit-create-button" onClick={handleSave}>
                          Save changes
                        </button>
                      </div>
                    </div>
                  )}

                  {activeTab === 'cards' && (
                    <div className="cards-section deck-editor-tab-panel deck-editor-view-snippets">
                      {cards.length === 0 ? (
                        <div className="deck-editor-view-snippets__empty">
                          <div className="no-cards">
                            No snippets in this cookbook yet. Open the “Add snippet” step to create one.
                          </div>
                        </div>
                      ) : (
                        <>
                          <ul className="user-profile-achievement-stats-panel__snippet-list deck-editor-view-snippets__snippet-list">
                            {cards
                              .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                              .map((card) => {
                                const isExpanded = expandedCards.has(card.id);
                                const langIcon = deckEditorLanguageIconName(
                                  card.language || deck?.language || ''
                                );
                                return (
                                  <li
                                    key={card.id}
                                    className="user-profile-achievement-stats-panel__snippet-row-item deck-editor-view-snippets__snippet-row-item"
                                  >
                                    <div className="user-profile-achievement-stats-panel__snippet-row deck-editor-view-snippets__snippet-row">
                                      <div
                                        className="user-profile-achievement-stats-panel__snippet-body deck-editor-view-snippets__snippet-main"
                                        onClick={() => {
                                          setExpandedCards((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(card.id)) next.delete(card.id);
                                            else next.add(card.id);
                                            return next;
                                          });
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setExpandedCards((prev) => {
                                              const next = new Set(prev);
                                              if (next.has(card.id)) next.delete(card.id);
                                              else next.add(card.id);
                                              return next;
                                            });
                                          }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={isExpanded}
                                        aria-label={
                                          isExpanded ? 'Collapse snippet details' : 'Expand snippet details'
                                        }
                                      >
                                        <div className="user-profile-achievement-stats-panel__snippet-title-row">
                                          <IconRenderer
                                            name={langIcon}
                                            type="language"
                                            size="small"
                                            className="user-profile-achievement-stats-panel__snippet-lang-icon"
                                            aria-hidden
                                          />
                                          <span className="user-profile-achievement-stats-panel__snippet-title">
                                            {card.title}
                                          </span>
                                        </div>
                                        <div className="user-profile-achievement-stats-panel__snippet-deck-row">
                                          <DeckIcon
                                            className="user-profile-achievement-stats-panel__snippet-deck-icon"
                                            aria-hidden
                                          />
                                          <span className="user-profile-achievement-stats-panel__snippet-deck-path">
                                            {card.description || 'No description'}
                                          </span>
                                        </div>
                                      </div>
                                      <div
                                        className="deck-editor-view-snippets__snippet-actions"
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingCard(card);
                                            setActiveTab('add');
                                          }}
                                          className="deck-editor-view-snippets__action deck-editor-view-snippets__action--edit"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteCard(card.id);
                                          }}
                                          className="deck-editor-view-snippets__action deck-editor-view-snippets__action--delete"
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </div>
                                    {isExpanded && card.solution ? (
                                      <div className="deck-editor-view-snippets__solution-panel">
                                        <pre className="deck-editor-view-snippets__solution-pre">{card.solution}</pre>
                                      </div>
                                    ) : null}
                                  </li>
                                );
                              })}
                          </ul>
                          <div className="deck-editor-view-snippets__pagination">
                            <div className="pagination">
                              <button
                                type="button"
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="pagination-button"
                              >
                                Previous
                              </button>
                              <span className="page-info">
                                Page {currentPage} of {Math.ceil(cards.length / itemsPerPage)}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  setCurrentPage((prev) =>
                                    Math.min(prev + 1, Math.ceil(cards.length / itemsPerPage))
                                  )
                                }
                                disabled={currentPage === Math.ceil(cards.length / itemsPerPage)}
                                className="pagination-button"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'add' && (
                    <div className="add-card-section deck-editor-tab-panel">
                      <CardManager
                        userId={userId}
                        deckId={deckId}
                        onCardAdded={() => {
                          handleCardAdded();
                          setActiveTab('cards');
                        }}
                        editingCard={editingCard}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="full-page-creator-mode-rail-placeholder" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
};

export default DeckEditor;
